import React, { useState, useEffect } from "react";
import { Trans, t } from "@lingui/macro";
import PropTypes from "prop-types";

import { i18n } from "base/i18n";
import { useStore } from "base/basestore";
import { getDayjsObject } from "base/utils";
import { Eye } from "kfcomponents/eye";
import {
  SearchInput,
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES
} from "widgets/inputs";
import { Icon, Badge, BADGE_TYPES } from "widgets/datadisplay";
import { Tooltip } from "widgets/feedback";
import { DateRangeDropdown } from "widgets/datetime";
import {
  BASE_DROPDOWN_POSITION,
  BASE_DROPDOWN_TRIGGER
} from "widgets/basedropdown";

import { BulkActionsToolbar } from "../bulk_actions_toolbar";
import {
  DUEDATE_FILTER,
  getTommorow,
  getLastYear,
  getDateRange
} from "../../metrics/util";

import styles from "./toolbar.css";

import EyeIcon from "../../myitems/icons/eye.svg";
import FilterIcon from "../../myitems/icons/FilterIcon.svg";
import CalendarIcon from "../../metrics/icons/calendar.svg";
import DownArrowIcon from "../../metrics/icons/downArrow.svg";
import GreenTickIcon from "../../metrics/icons/greenTick.svg";

Toolbar.propTypes = {
  fields: PropTypes.array,
  showMore: PropTypes.bool,
  title: PropTypes.string,
  onSearch: PropTypes.func,
  onShowMoreItemAction: PropTypes.func,
  onPreferenceUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  showFilterOption: PropTypes.bool,
  showEye: PropTypes.bool,
  onBulkApprove: PropTypes.func,
  actionButtons: PropTypes.array,
  onBulkReject: PropTypes.array,
  onBulkReassign: PropTypes.array,
  onMarkAsCompleted: PropTypes.array,
  parentComponent: PropTypes.string,
  simpleFilterSelected: PropTypes.object,
  onSimpleFilterChange: PropTypes.func,
  searchValue: PropTypes.string
};

export function Toolbar({
  fields = [],
  title,
  onSearch,
  onPreferenceUpdate,
  onDelete,
  showSearch = true,
  showFilterOption = true,
  showEye = true,
  onBulkApprove,
  onBulkReject,
  onBulkReassign,
  onMarkAsCompleted,
  actionButtons,
  parentComponent,
  simpleFilterSelected,
  onSimpleFilterChange,
  disabledColumnIds = [],
  searchValue = ""
}) {
  const isIconButtons = window.isLeftNavEnabled;
  const { useStateStore: useGridStore } = useStore();

  const [allFields, setFields] = useState(fields);
  const showFilter = useGridStore((state) => state.showFilter);
  const gridData = useGridStore((state) => state.gridData);
  const appliedFilterCount =
    gridData.Filter &&
    ((gridData.Filter["AND"] && gridData.Filter["AND"].length) ||
      (gridData.Filter["OR"] && gridData.Filter["OR"].length));

  const configJson = {
    Columns: gridData.Columns,
    Filter: gridData.Filter,
    FilterType: gridData.FilterType,
    PageSize: gridData.PageSize,
    Sort: gridData.Sort
  };
  const selectedRowsIds = useGridStore((state) => state.selectedRowsIds);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("LAST_30_DAYS");
  const [range, setRange] = useState(null);
  const endDate = getTommorow();
  const startDate = getLastYear();

  function eyeFilterOnSearch(value = "") {
    let searchValue = value && value.trim();
    const filterList =
      fields &&
      fields.filter(
        (data) =>
          data.Name.toLowerCase() &&
          data.Name.toLowerCase().includes(searchValue.toLowerCase())
      );
    setFields(filterList);
  }
  useEffect(
    function updateFields() {
      setFields(fields || []);
    },
    [fields]
  );

  useEffect(
    function onFilterChange() {
      if (simpleFilterSelected?.key) {
        setSelectedFilter(simpleFilterSelected.key);
        if (simpleFilterSelected.key === "CUSTOM_PERIOD") {
          let start = getDayjsObject(simpleFilterSelected?.start);
          let end = getDayjsObject(simpleFilterSelected?.end);
          setRange({
            start: start,
            end: end,
            label: `${simpleFilterSelected.start} to ${simpleFilterSelected.end}`
          });
        } else {
          setRange(null);
        }
      }
    },
    [simpleFilterSelected]
  );

  const onColumnVisibility = (ids) => {
    let newConfigJson = { ...configJson };
    let selectedColumns = [];
    gridData.Columns.forEach((column) => {
      if (ids.includes(column.Id)) {
        selectedColumns.push({ Id: column.Id, Width: column.Width });
        const index = ids.indexOf(column.Id);
        ids.splice(index, 1);
      }
    });
    const remainingColumns = ids.map((id) => {
      return { Id: id };
    });
    selectedColumns = selectedColumns.concat(remainingColumns);
    newConfigJson.Columns = selectedColumns;
    setFields(fields);
    onPreferenceUpdate({ config: newConfigJson });
  };

  function ondropdownclick(key) {
    if (key === "CUSTOM_PERIOD" && range?.start && range?.end) {
      onSimpleFilterChange({
        key: key,
        start: range?.start,
        end: range?.end
      });
    } else if (key !== "CUSTOM_PERIOD") {
      let _range = getDateRange(key);
      onSimpleFilterChange({
        key: key,
        start: _range.start_date,
        end: _range.end_date
      });
    }
    setIsOpen(false);
  }

  return (
    <React.Fragment>
      <div>
        <div className={styles.toolbar}>
          <div className={`h6-600`}>{title}</div>
          <div className={styles.actions}>
            {showSearch && (
              <SearchInput
                onChange={(event) => {
                  onSearch(event.target.value);
                  selectedRowsIds.length &&
                    useGridStore.getState().setSelectedRowsIds([]);
                }}
                className={styles.search}
                placeholder={i18n._(t`Search`)}
                onClear={() => {
                  onSearch("");
                }}
                value={searchValue}
              />
            )}
            {showFilterOption && (
              <div>
                {!isIconButtons ? (
                  <Button
                    type={BUTTON_TYPES.ACTION}
                    size={BUTTON_SIZES.SMALL}
                    leftIcon={
                      <Icon paddingNone={true}>
                        <FilterIcon />
                      </Icon>
                    }
                    rightIcon={
                      <Badge
                        count={appliedFilterCount}
                        maxCount={100}
                        type={BADGE_TYPES.MAGENTA}
                      />
                    }
                    onClick={() => {
                      useGridStore.getState().setShowFilter(!showFilter);
                    }}
                  >
                    <div className={"smallplus"}>
                      <Trans>Filter</Trans>
                    </div>
                  </Button>
                ) : (
                  <Tooltip content={i18n._(t`Filter`)}>
                    <Button
                      type={BUTTON_TYPES.ACTION}
                      size={BUTTON_SIZES.SMALL}
                      className={styles.filterbutton}
                      rightIcon={
                        <Badge
                          count={appliedFilterCount}
                          maxCount={100}
                          type={BADGE_TYPES.MAGENTA}
                        />
                      }
                      onClick={() => {
                        useGridStore.getState().setShowFilter(!showFilter);
                      }}
                    >
                      <Icon paddingNone={true}>
                        <FilterIcon />
                      </Icon>
                    </Button>
                  </Tooltip>
                )}
              </div>
            )}
            {showEye &&
              allFields?.length > 0 &&
              (!isIconButtons ? (
                <div>
                  <Eye
                    disabledIds={disabledColumnIds}
                    targetRenderer={() => (
                      <Button
                        type={BUTTON_TYPES.ACTION}
                        size={BUTTON_SIZES.SMALL}
                        leftIcon={
                          <Icon paddingNone={true}>
                            <EyeIcon />
                          </Icon>
                        }
                      >
                        <div className={"smallplus"}>
                          <Trans>Show/hide fields</Trans>
                        </div>
                      </Button>
                    )}
                    onSearch={eyeFilterOnSearch}
                    dataSource={allFields}
                    onApply={(selectedFieds) =>
                      onColumnVisibility(selectedFieds.map((field) => field.Id))
                    }
                    labelKey={"Name"}
                    max={100}
                    selectedIds={
                      gridData.Columns
                        ? gridData.Columns.map((field) => field.Id)
                        : []
                    }
                  />
                </div>
              ) : (
                <Tooltip content={i18n._(t`Show/hide fields`)}>
                  <div className={styles.eye}>
                    <Eye
                      targetRenderer={() => (
                        <Button
                          type={BUTTON_TYPES.ACTION}
                          size={BUTTON_SIZES.SMALL}
                        >
                          <Icon paddingNone={true}>
                            <EyeIcon />
                          </Icon>
                        </Button>
                      )}
                      onSearch={eyeFilterOnSearch}
                      dataSource={allFields}
                      onApply={(selectedFieds) =>
                        onColumnVisibility(
                          selectedFieds.map((field) => field.Id)
                        )
                      }
                      labelKey={"Name"}
                      max={100}
                      selectedIds={
                        gridData.Columns
                          ? gridData.Columns.map((field) => field.Id)
                          : []
                      }
                      disabledIds={disabledColumnIds}
                    />
                  </div>
                </Tooltip>
              ))}
            {onSimpleFilterChange && simpleFilterSelected && (
              <DateRangeDropdown
                endsAt={endDate}
                startsAt={startDate}
                targetRenderer={() => (
                  <div
                    id="dropDownRenderer"
                    className={styles.dropDownRenderer}
                    onClick={() => setIsOpen(true)}
                  >
                    <div className={styles.dropDownContent}>
                      <Icon paddingNone={true}>
                        <CalendarIcon />
                      </Icon>
                      <div
                        className={`smallplus`}
                        style={{ color: "var(--text-gray-300)" }}
                      >
                        <Trans>Created in</Trans>&nbsp;&nbsp;
                      </div>
                      <div className={`smallplus ${styles.dropFilter}`}>
                        {range && range.label
                          ? range.label
                          : i18n._(DUEDATE_FILTER[selectedFilter])}
                      </div>
                    </div>
                    <Icon paddingNone={true}>
                      <DownArrowIcon />
                    </Icon>
                  </div>
                )}
                // open={true}
                isOpen={isOpen}
                position={BASE_DROPDOWN_POSITION.BOTTOM}
                trigger={BASE_DROPDOWN_TRIGGER.CLICK}
                listRenderer={({ closePopup, switchToRangeSelector }) =>
                  Object.keys(DUEDATE_FILTER).map((key) => {
                    const dropDownElement =
                      document.getElementById("dropDownRenderer");
                    return (
                      <div
                        key={key}
                        id="globalMyTaskDueFilter"
                        className={`base ${styles.dropData}`}
                        style={{
                          width: `${
                            dropDownElement ? dropDownElement.offsetWidth : null
                          }px`
                        }}
                        onClick={() => {
                          setIsOpen(false);
                          // setIsLoading(true);
                          setSelectedFilter(key);
                          // onFilterClick({ action: "filter", id: key });
                          if (
                            DUEDATE_FILTER[key] === DUEDATE_FILTER.CUSTOM_PERIOD
                          ) {
                            switchToRangeSelector();
                          } else {
                            setRange(null);
                          }
                          ondropdownclick(key);
                        }}
                      >
                        {i18n._(DUEDATE_FILTER[key])}
                        {key === selectedFilter && (
                          <Icon paddingNone={true}>
                            <GreenTickIcon />
                          </Icon>
                        )}
                      </div>
                    );
                  })
                }
                onChange={(range) => {
                  setRange(range);
                  range?.start &&
                    range?.end &&
                    onSimpleFilterChange({
                      key: selectedFilter,
                      start: range?.start,
                      ens: range?.end
                    });
                }}
              />
            )}
          </div>
        </div>
        {selectedRowsIds.length > 0 && actionButtons?.length && (
          <BulkActionsToolbar
            onBulkApprove={onBulkApprove}
            onBulkReject={onBulkReject}
            actionButtons={actionButtons}
            onBulkReassign={onBulkReassign}
            onMarkAsCompleted={onMarkAsCompleted}
            onDelete={onDelete}
            parentComponent={parentComponent}
          />
        )}
      </div>
    </React.Fragment>
  );
}
